/**
 * Theme Switcher Component
 *
 * Dropdown for switching between light, dark, and system themes.
 */

import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, ChevronDown } from 'lucide-react';
import { useTheme, Theme } from '@/lib/theme';
import { useI18n } from '@/lib/i18n';
import './ThemeSwitcher.scss';

interface ThemeSwitcherProps {
  variant?: 'compact' | 'full';
  className?: string;
}

const themeIcons: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export function ThemeSwitcher({ variant = 'compact', className = '' }: ThemeSwitcherProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const themes: Theme[] = ['light', 'dark', 'system'];

  // Get the icon to display based on current state
  const CurrentIcon = theme === 'system'
    ? (resolvedTheme === 'dark' ? Moon : Sun)
    : themeIcons[theme];

  const handleSelect = (newTheme: Theme) => {
    setTheme(newTheme);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`theme-switcher ${className}`}>
      <button
        className={`theme-switcher__btn theme-switcher__btn--${variant}`}
        onClick={() => setOpen(!open)}
        aria-label={t('theme.label', 'Theme')}
        aria-expanded={open}
      >
        <CurrentIcon size={18} />
        {variant === 'full' && (
          <>
            <span>{t(`theme.${theme}`, theme)}</span>
            <ChevronDown size={14} />
          </>
        )}
        {variant === 'compact' && <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="theme-switcher__dropdown">
          {themes.map((t_) => {
            const Icon = themeIcons[t_];
            return (
              <button
                key={t_}
                className={`theme-switcher__option ${theme === t_ ? 'theme-switcher__option--active' : ''}`}
                onClick={() => handleSelect(t_)}
              >
                <Icon size={16} />
                <span>{t(`theme.${t_}`, t_)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
